import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { codeForPath, tournamentDetailId } from "../lib/adminMenuCatalog";

/**
 * <ProtectedRoute roles={["super_admin", "organizer"]} perms={["users:read"]}>
 *   <AdminUsersPage />
 * </ProtectedRoute>
 *
 *   - Waiting for auth → renders null (layout decides skeleton)
 *   - Not logged in    → redirect to /login (preserves returnTo)
 *   - Lacking role/perm → redirect to /403
 *   - Phase 85.A.239: với path /admin/* có trong menu catalog, `menu_access`
 *     là quyền quyết định (vượt trên role prop) — cấp menu = truy cập được.
 */
export default function ProtectedRoute({ children, roles, perms }) {
  const { user, loading, hasRole, hasPermission, hasMenu } = useAuth();
  const location = useLocation();

  if (loading) return null;

  if (!user) {
    return <Navigate to="/login" state={{ returnTo: location.pathname + location.search }} replace />;
  }

  const userRoles = user.roles || [];
  const onAdminPath = location.pathname.startsWith("/admin");
  const onRefereePath = location.pathname.startsWith("/referee");
  const isAthlete = userRoles.includes("athlete");
  const hasAdmin =
    userRoles.includes("super_admin") ||
    userRoles.includes("organizer") ||
    userRoles.includes("admin") ||
    userRoles.includes("staff");
  const hasReferee = userRoles.includes("referee");
  const isRefereeOnly = hasReferee && !hasAdmin;
  const hasAnyMenu = (user.menu_access || []).length > 0;
  const menuCode = onAdminPath ? codeForPath(location.pathname) : null;

  // Phase 85.A.275 (2a) — User được gán quyền theo GIẢI cụ thể (tab "Quyền theo
  // giải đấu") mở được link chi tiết giải đó dù menu/role bị giới hạn. Không chặn
  // sớm ở FE; backend tự enforce per-tid (user_can_access_tournament_async).
  const assignedTids = user.assigned_tournament_ids || [];
  const detailTid = onAdminPath ? tournamentDetailId(location.pathname) : null;
  if (detailTid && assignedTids.includes(detailTid)) {
    return children;
  }

  // Chặn user không đặc quyền & KHÔNG được cấp menu nào khỏi /admin, /referee.
  if (isAthlete && !hasAdmin && !hasReferee && !hasAnyMenu && (onAdminPath || onRefereePath)) {
    return <Navigate to="/" replace />;
  }
  if (isRefereeOnly && onAdminPath && !hasAnyMenu) {
    return <Navigate to="/referee" replace />;
  }

  // Phase 85.A.275 — Mirror CHÍNH XÁC backend middleware `_menu_permission_guard`
  // (server.py): chỉ enforce menu khi user có OVERRIDE tường minh (super_admin
  // đã phân quyền riêng) HOẶC user KHÔNG thuộc admin-tier. Organizer/admin/staff
  // KHÔNG override → giữ hành vi role-based (backend cho qua) → FE cũng phải cho
  // qua, nếu không FE chặt hơn BE → 403 oan khi click deep-link (Telegram/Zalo)
  // tới /admin/tournaments/:id dù backend authorize được giải đó.
  const ADMIN_TIER = ["super_admin", "organizer", "admin", "staff"];
  const isAdminTier = userRoles.some((r) => ADMIN_TIER.includes(r));
  const hasMenuOverride = Array.isArray(user.menu_permissions);
  const enforceMenu = hasMenuOverride || !isAdminTier;
  if (onAdminPath && menuCode && enforceMenu) {
    return hasMenu(menuCode) ? children : <Navigate to="/403" replace />;
  }

  // Path không thuộc catalog: giữ gác theo role/perm (tương thích cũ).
  if (roles && roles.length > 0 && !hasRole(...roles)) {
    return <Navigate to="/403" replace />;
  }
  if (perms && perms.length > 0 && !hasPermission(...perms)) {
    return <Navigate to="/403" replace />;
  }

  return children;
}
